import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../helpers/constants";
import apiInstance from "../../instances/apiInstance";
import {
  CreateSystemMessageParams,
  SystemMessageDetailParams,
  SystemMessageListParams,
} from "./models";

export const useGetSystemMessageListQuery = () => {
  const [params, setParams] = useState<SystemMessageListParams>();

  const query = useQuery({
    queryKey: ["useGetSystemMessageListQuery", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { countryCode = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_SYSTEM_MESSAGE_LIST_BY_COUNTRY_IDS(countryCode),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: SystemMessageListParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetSystemMessageCountryQuery = () => {
  return useQuery({
    queryKey: ["getSystemMessageCountryQuery"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_SYSTEM_MESSAGE_COUNTRY_LIST,
      );
      return res.data?.countryLocaleCodes || [];
    },
    retry: false,
  });
};

export const useCreateSystemMessageMutation = () => {
  return useMutation({
    mutationFn: (params: CreateSystemMessageParams) => {
      return apiInstance.post(API_ENDPOINTS.CREATE_SYSTEM_MESSAGE, params);
    },
  });
};

export const useDeleteSystemMessageMutation = () => {
  return useMutation({
    mutationFn: (messageId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.DELETE_SYSTEM_MESSAGE(messageId)}`,
      );
    },
  });
};

export const useEditSystemMessageMutation = () => {
  return useMutation({
    mutationFn: (params: CreateSystemMessageParams) => {
      return apiInstance.put(
        API_ENDPOINTS.EDIT_SYSTEM_MESSAGE(params.id),
        params,
      );
    },
  });
};

export const useGetSystemMessageDetailsQuery = () => {
  const [params, setParams] = useState<SystemMessageDetailParams>();

  const query = useQuery({
    queryKey: ["getSystemMessageDetailsQuery", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { id = "" } = params;
      const res = await apiInstance.get(API_ENDPOINTS.VIEW_SYSTEM_MESSAGE(id));
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: SystemMessageDetailParams) => {
    setParams({
      ...newParams,
      isRefetch: params?.isRefetch === undefined ? true : !params.isRefetch,
    });
  };

  return { ...query, refetchWithParams };
};
